import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, Copy, Building2, CreditCard, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentInstructionsPage = () => {
  const location = useLocation();
  const orderData = location.state?.orderData;
  const [paymentMethod, setPaymentMethod] = useState('eft'); // 'eft' or 'ozow'
  const [processingOzow, setProcessingOzow] = useState(false);

  if (!orderData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <p className="text-xl text-gray-500 mb-4">No order information found</p>
          <Link to="/products">
            <Button className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleOzowPayment = async () => {
    setProcessingOzow(true);
    try {
      const response = await axios.post(`${API_URL}/ozow/create-payment`, {
        amount: orderData.total,
        transaction_reference: orderData.order_number,
        bank_reference: orderData.order_number,
        customer_name: orderData.customer?.name || '',
        customer_email: orderData.customer?.email || '',
        order_id: orderData.id || orderData.order_number,
        is_test: false
      });

      if (response.data.payment_url) {
        // Redirect to OZOW payment page
        window.location.href = response.data.payment_url;
      } else {
        toast.error('Failed to initiate OZOW payment');
        setProcessingOzow(false);
      }
    } catch (error) {
      console.error('OZOW payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessingOzow(false);
    }
  };

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-[#0A2342]">
                Order Confirmed!
              </h1>
              <p className="text-gray-600">Order #{orderData.order_number}</p>
            </div>
          </div>

          {/* Order Summary */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <h3 className="text-lg font-heading font-semibold text-[#0A2342] mb-4">
              Order Summary
            </h3>
            <div className="space-y-2">
              {orderData.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-gray-700">{item.name} x{item.quantity}</span>
                  <span className="font-mono-accent">R{item.total?.toFixed(2)}</span>
                </div>
              ))}
              {orderData.membership && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">{orderData.membership}</span>
                  <span className="font-mono-accent">Included</span>
                </div>
              )}
              {orderData.discount > 0 && (
                <div className="flex justify-between text-sm text-[#FF6B35]">
                  <span>Member Discount</span>
                  <span className="font-mono-accent">-R{orderData.discount?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold pt-2 border-t border-gray-200">
                <span className="text-[#0A2342]">Total Amount</span>
                <span className="font-mono-accent text-[#0A2342]">R{orderData.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="mb-8">
            <h3 className="text-xl font-heading font-semibold text-[#0A2342] mb-4">
              Choose Payment Method
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* OZOW Option */}
              <button
                onClick={() => setPaymentMethod('ozow')}
                className={`p-6 rounded-lg border-2 text-left transition-all ${
                  paymentMethod === 'ozow'
                    ? 'border-[#FF6B35] bg-[#FFF4ED]'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                data-testid="ozow-payment-option"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    paymentMethod === 'ozow' ? 'bg-[#FF6B35]' : 'bg-gray-100'
                  }`}>
                    <Zap className={`w-5 h-5 ${paymentMethod === 'ozow' ? 'text-white' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0A2342]">Pay with OZOW</h4>
                    <p className="text-sm text-gray-500">Instant Bank Payment</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Pay instantly from your bank account. Fast, secure, and no card needed.
                </p>
              </button>

              {/* EFT Option */}
              <button
                onClick={() => setPaymentMethod('eft')}
                className={`p-6 rounded-lg border-2 text-left transition-all ${
                  paymentMethod === 'eft'
                    ? 'border-[#0A2342] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                data-testid="eft-payment-option"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    paymentMethod === 'eft' ? 'bg-[#0A2342]' : 'bg-gray-100'
                  }`}>
                    <Building2 className={`w-5 h-5 ${paymentMethod === 'eft' ? 'text-white' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0A2342]">Manual EFT</h4>
                    <p className="text-sm text-gray-500">Bank Transfer</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Transfer funds manually using the banking details provided.
                </p>
              </button>
            </div>
          </div>

          {/* OZOW Payment Section */}
          {paymentMethod === 'ozow' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-[#FF6B35] to-[#FF8F5C] rounded-lg p-6 text-white">
                <h3 className="text-xl font-heading font-semibold mb-2">
                  Pay Instantly with OZOW
                </h3>
                <p className="text-sm opacity-90 mb-4">
                  Click the button below to securely pay using your bank account. You'll be redirected to OZOW to complete the payment.
                </p>
                <Button
                  onClick={handleOzowPayment}
                  disabled={processingOzow}
                  className="w-full bg-white text-[#FF6B35] hover:bg-gray-100 font-semibold py-6 text-lg"
                  data-testid="pay-with-ozow-btn"
                >
                  {processingOzow ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay R{orderData.total?.toFixed(2)} with OZOW
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 text-center">
                You will be securely redirected to OZOW to complete your payment.
              </p>
            </div>
          )}

          {/* EFT Banking Details */}
          {paymentMethod === 'eft' && (
            <div className="space-y-6">
              <div className="bg-[#F0EFEA] p-6 rounded-lg">
                <h2 className="text-xl font-heading font-semibold text-[#0A2342] mb-4">
                  EFT Payment Instructions
                </h2>
                <p className="text-gray-700 mb-4">
                  Please make an EFT payment to the account below. Once payment is made, please email your proof of payment to <a href="mailto:info@justdive.co.za" className="text-[#FF6B35] font-semibold hover:underline">info@justdive.co.za</a>
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Important:</strong> Shipping will commence only after payment is confirmed in our account.
                </p>
              </div>

              {/* ABSA Account */}
              <div className="border-2 border-[#0A2342] rounded-lg p-6 bg-white">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-6 h-6 text-[#0A2342]" strokeWidth={1.5} />
                  <h4 className="text-lg font-heading font-semibold text-[#0A2342]">
                    ABSA Bank
                  </h4>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Account Name:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">JUST DIVE NPC</span>
                      <button onClick={() => copyToClipboard('JUST DIVE NPC', 'Account name')} className="text-[#0A2342] hover:text-[#FF6B35]">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Account Number:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-accent font-semibold">4125123307</span>
                      <button onClick={() => copyToClipboard('4125123307', 'Account number')} className="text-[#0A2342] hover:text-[#FF6B35]">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Branch Code:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-accent font-semibold">632005</span>
                      <button onClick={() => copyToClipboard('632005', 'Branch code')} className="text-[#0A2342] hover:text-[#FF6B35]">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#FFF4ED] border border-[#FF6B35] rounded-lg p-6">
                <p className="text-sm text-gray-700">
                  <strong>Reference:</strong> Use Order #{orderData.order_number} as your payment reference
                </p>
              </div>
            </div>
          )}

          {/* Customer Details */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-heading font-semibold text-[#0A2342] mb-4">
              Shipping Details
            </h3>
            <div className="space-y-2 text-sm">
              <p><strong>Name:</strong> {orderData.customer?.name}</p>
              <p><strong>Email:</strong> {orderData.customer?.email}</p>
              <p><strong>Contact:</strong> {orderData.customer?.contact}</p>
              <p><strong>Address:</strong> {orderData.customer?.address}</p>
            </div>
          </div>

          <div className="mt-8 flex gap-4">
            <Link to="/dashboard" className="flex-1">
              <Button className="w-full bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="view-dashboard-btn">
                View Dashboard
              </Button>
            </Link>
            <Link to="/products" className="flex-1">
              <Button variant="outline" className="w-full" data-testid="continue-shopping-btn">
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentInstructionsPage;
